import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Bell, RotateCw, Send } from 'lucide-react';
import { formatLongDate } from '@unifemga/shared';
import { api } from '@/lib/api';
import {
  Button,
  Card,
  CardContent,
  EmptyState,
  Field,
  PageHeader,
  Pagination,
  Select,
  Skeleton,
  Stat,
  Table,
  Td,
  Th,
  type PageMeta,
} from '@/components/ui';
import { StatusBadge } from '@/components/StatusBadge';
import { NOTIFICATION_STATUS } from '@/lib/status';

interface NotificationRow {
  id: string;
  event: string;
  channel: string;
  status: string;
  recipient: string;
  subject: string | null;
  memberName: string | null;
  scheduledFor: string | null;
  sentAt: string | null;
  deliveredAt: string | null;
  failedAt: string | null;
  retryCount: number;
  lastError: string | null;
  smsSegments: number | null;
  createdAt: string;
}

export default function AdminNotifications() {
  const queryClient = useQueryClient();
  const [channel, setChannel] = useState('');
  const [status, setStatus] = useState('');
  const [page, setPage] = useState(1);

  const { data, isLoading } = useQuery({
    queryKey: ['notifications', page, channel, status],
    queryFn: () =>
      api.get<{ data: NotificationRow[]; meta: PageMeta }>(
        `/notifications?pageSize=25&page=${page}${channel ? `&channel=${channel}` : ''}${
          status ? `&status=${status}` : ''
        }`,
      ),
  });

  const flush = useMutation({
    mutationFn: () => api.post<{ sent: number; failed: number }>('/jobs/dispatch-notifications'),
    onSuccess: (result) => {
      toast.success(`Dispatched ${result.sent}, ${result.failed} failure(s).`);
      void queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
    onError: (error) => toast.error(error instanceof Error ? error.message : 'Failed.'),
  });

  const retry = useMutation({
    mutationFn: (id: string) => api.post(`/notifications/${id}/retry`),
    onSuccess: () => {
      toast.success('Requeued.');
      void queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });

  const failed = data?.data.filter((n) => n.status === 'FAILED').length ?? 0;
  const smsSegments = data?.data.reduce((total, n) => total + (n.smsSegments ?? 0), 0) ?? 0;

  return (
    <div>
      <PageHeader
        eyebrow="Communications"
        title="Notification log"
        description="Every message the system has queued, sent or failed to send."
        actions={
          <Button variant="outline" loading={flush.isPending} onClick={() => flush.mutate()}>
            <Send size={16} /> Send queued now
          </Button>
        }
      />

      {data && (
        <div className="mb-6 grid gap-4 sm:grid-cols-3">
          <Stat label="Messages" value={data.meta.total} icon={<Bell size={16} />} />
          <Stat
            label="Failed"
            value={failed}
            tone={failed > 0 ? 'danger' : 'success'}
            hint={failed > 0 ? 'Needs attention' : 'All delivered'}
          />
          <Stat
            label="SMS segments"
            value={smsSegments}
            hint="On this page — providers bill per segment"
          />
        </div>
      )}

      <Card className="mb-6">
        <CardContent className="flex flex-wrap items-end gap-4 pt-6">
          <Field label="Channel" htmlFor="channel" className="w-48">
            <Select id="channel" value={channel} onChange={(e) => { setChannel(e.target.value); setPage(1); }}>
              <option value="">All channels</option>
              {['EMAIL', 'SMS', 'WHATSAPP', 'IN_APP'].map((option) => (
                <option key={option} value={option}>
                  {option.replace(/_/g, '-').toLowerCase()}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Status" htmlFor="status" className="w-48">
            <Select id="status" value={status} onChange={(e) => { setStatus(e.target.value); setPage(1); }}>
              <option value="">All statuses</option>
              {Object.entries(NOTIFICATION_STATUS).map(([key, presentation]) => (
                <option key={key} value={key}>
                  {presentation.label}
                </option>
              ))}
            </Select>
          </Field>
        </CardContent>
      </Card>

      {isLoading ? (
        <Skeleton className="h-64" />
      ) : !data || data.data.length === 0 ? (
        <EmptyState
          icon={<Bell size={20} />}
          title="No notifications"
          description="Messages appear here as the system sends them."
        />
      ) : (
        <Card>
          <CardContent className="px-0">
            <Table>
              <thead>
                <tr>
                  <Th>Event</Th>
                  <Th>Channel</Th>
                  <Th>Recipient</Th>
                  <Th>Member</Th>
                  <Th>Status</Th>
                  <Th numeric>Attempts</Th>
                  <Th>When</Th>
                  <Th />
                </tr>
              </thead>
              <tbody>
                {data.data.map((notification) => (
                  <tr key={notification.id} className="transition-colors hover:bg-muted/50">
                    <Td>
                      <p className="text-sm font-medium text-foreground">
                        {humanise(notification.event)}
                      </p>
                      {notification.subject && (
                        <p className="max-w-xs truncate text-xs text-foreground-secondary">
                          {notification.subject}
                        </p>
                      )}
                    </Td>
                    <Td className="text-foreground-secondary">
                      {notification.channel.replace(/_/g, '-').toLowerCase()}
                      {notification.smsSegments ? ` (${notification.smsSegments})` : ''}
                    </Td>
                    <Td className="max-w-[180px] truncate text-xs text-foreground-secondary">
                      {notification.recipient}
                    </Td>
                    <Td className="text-sm">{notification.memberName ?? '—'}</Td>
                    <Td>
                      <StatusBadge map={NOTIFICATION_STATUS} status={notification.status} />
                      {notification.lastError && (
                        <p className="mt-1 max-w-[200px] truncate text-xs text-danger">
                          {notification.lastError}
                        </p>
                      )}
                    </Td>
                    <Td numeric>{notification.retryCount}</Td>
                    <Td className="numeric text-xs text-foreground-secondary">
                      {formatLongDate(
                        new Date(notification.sentAt ?? notification.createdAt),
                      )}
                    </Td>
                    <Td>
                      {notification.status === 'FAILED' && (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => retry.mutate(notification.id)}
                          aria-label="Retry"
                        >
                          <RotateCw size={14} />
                        </Button>
                      )}
                    </Td>
                  </tr>
                ))}
              </tbody>
            </Table>
            <Pagination meta={data.meta} onPageChange={setPage} />
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function humanise(value: string): string {
  const text = value.replace(/_/g, ' ').toLowerCase();
  return text.charAt(0).toUpperCase() + text.slice(1);
}
